import { useScopeQuery } from "@/core/store/scopeQuery";
import { arrayIndex } from "@/core/util/arrayIndex";
import { Modificator } from "@/gql/gen";
import { useCatalogStore } from "@/modules/shop/store/catalog";
import { ProductModificatorDescriptor } from "@/modules/shop/store/util/catalog";
import { checkOrderRestriction, RestrictionFailureReason } from "@/modules/shop/util/checkOrderRestriction";
import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

const STORE_NAME = "shop-product-modificator";

/**
 * Причина неактивности модификатора
 * Расширяет RestrictionFailureReason дополнительными причинами специфичными для модификаторов
 */
export type ModificatorInactiveReason = 
    | RestrictionFailureReason  // Все причины из checkOrderRestriction
    | 'feature'                 // Неактивная фича
    | 'inactive';               // Модификатор неактивен (ACTIVE=false)

/**
 * Результат проверки условий модификатора
 */
export interface ModificatorConditionCheckResult {
    /** Модификатор активен (условия выполнены) */
    isActive: boolean;
    /** Причина неактивности (если isActive = false) */
    inactiveReason: ModificatorInactiveReason | null;
}

/**
 * Store для работы с модификаторами товаров
 *
 * Управляет загрузкой и индексацией модификаторов.
 * Предоставляет индексы для быстрого доступа:
 * - По ID модификатора
 * - По CODE_PATH
 * - По GROUP_CODE (для работы с корзиной)
 * - По ID товара (с учетом наследования от секций)
 * - С фильтрацией по условиям (CONDITION)
 */
export const useProductModificatorStore = defineStore(STORE_NAME, () => {
    const catalogStore = useCatalogStore();

    const {products, sectionsPaths} = storeToRefs(catalogStore);

    const {registerScopeQuery} = useScopeQuery();
    
    /**
     * Функция для проверки условий модификатора
     * Используется для фильтрации модификаторов по CONDITION
     * 
     * @param modificator Модификатор для проверки
     * @param context Контекст заказа (attrs, office, basketStat, basketItems, orderPriceBeforeDiscounts, orderPriceAfterDiscounts, userPrizeElementId)
     * @returns Результат проверки с информацией об активности и причине неактивности
     */
    const checkModificatorCondition = (
        modificator: Modificator,
        context: {
            attrs: any;
            office?: any;
            basketStat: any;
            basketItems: any[];
            orderPriceBeforeDiscounts?: number;
            orderPriceAfterDiscounts?: number;
            userPrizeElementId?: number | null;
        }
    ): ModificatorConditionCheckResult => {
        // Проверяем активность модификатора
        if (!modificator.ACTIVE) {
            return {
                isActive: false,
                inactiveReason: 'inactive'
            };
        }

        // Проверяем условия через checkOrderRestriction
        // Он сам проверит, заданы ли условия, и вернет isApplicable=true если условий нет
        const checkResult = checkOrderRestriction(modificator.CONDITION, context);
        
        return {
            isActive: checkResult.isApplicable,
            inactiveReason: checkResult.failureReason
        };
    };

    /** Список всех модификаторов, загруженных через GraphQL */
    const productModificators = ref<Modificator[]>([]);

    /** Индекс модификаторов по ID для быстрого доступа */
    const productModificatorsById = computed(() =>
        arrayIndex(productModificators.value, "ID")
    );

    /** Индекс модификаторов по CODE_PATH (например: "gifts.drink") */
    const productModificatorsByCodePath = computed(() =>
        arrayIndex(productModificators.value, "CODE_PATH")
    );

    /** Индекс модификаторов по GROUP_CODE (ключ для хранения в корзине) */
    const productModificatorsByGroupCode = computed(() =>
        arrayIndex(productModificators.value, "GROUP_CODE")
    );
    /**
     * Индекс модификаторов по ID товара (с учетом наследования от секций)
     *
     * Логика:
     * 1. Собираются модификаторы из всей цепочки родительских секций товара
     * 2. Добавляются модификаторы самого товара (могут перезаписать секционные)
     * 3. Дубликаты исключаются (используется объект как Set)
     *
     * Пример:
     * Секция "Пиццы" → MODIFICATORS: [1, 2, 3]
     *   └─ Секция "Мясные" → MODIFICATORS: [4, 5]
     *       └─ Товар "Пепперони" → MODIFICATORS: [6]
     * Результат: [1, 2, 3, 4, 5, 6]
     */
    const productModificatorsByProductId = computed(() => {
        return products.value.reduce<Record<string, Modificator[]>>(
            (map, product) => {
                const modificatorIds: Record<string, number> = {};

                // Модификаторы от секций (все родительские секции)
                if (product.IBLOCK_SECTION_ID) {
                    const sections = sectionsPaths.value[product.IBLOCK_SECTION_ID];
                    if (sections) {
                        sections.forEach((section) => {
                            section.PROPERTIES.MODIFICATORS.forEach((id) => {
                                modificatorIds[id] = id;
                            });
                        });
                    }
                }

                // Модификаторы самого товара
                product.PROPERTIES.MODIFICATORS.forEach((id) => {
                    modificatorIds[id] = id;
                });

                map[product.ID] = Object.values(modificatorIds).map(
                    (id) => productModificatorsById.value[id]!
                );
                return map;
            },
            {}
        );
    });

    /**
     * Получить модификатор по дескриптору
     *
     * ProductModificatorDescriptor может быть:
     * - Modificator (объект) - возвращается как есть
     * - number - ID модификатора
     * - string - CODE_PATH или GROUP_CODE модификатора
     *
     * @param modificator Дескриптор модификатора (объект, ID, CODE_PATH или GROUP_CODE)
     * @param productId ID товара
     * @returns Modificator | undefined
     */
    const getProductModificator = (modificator: ProductModificatorDescriptor, productId?: number) => {
        if (typeof modificator === "object") return modificator;

        if (productId) {
            const mods = productModificatorsByProductId.value[productId]
            if (mods) {
                return mods.find(mod => mod.ID === modificator || mod.GROUP_CODE === modificator || mod.CODE_PATH === modificator)
            }
        }

        if (productModificatorsById.value[modificator])
            return productModificatorsById.value[modificator];
        else if (productModificatorsByCodePath.value[modificator])
            return productModificatorsByCodePath.value[modificator];
        else if (productModificatorsByGroupCode.value[modificator])
            return productModificatorsByGroupCode.value[modificator];
    };

    // GETTERS

    // ACTIONS

    /**
     * Регистрация запроса для загрузки модификаторов при старте приложения
     *
     * Запрос выполняется один раз при инициализации приложения (scope: "app")
     * Все модификаторы загружаются сразу для оптимизации производительности
     */
    registerScopeQuery(
        STORE_NAME,
        "app",
        {
            product_modificator_list: {
                __fragment: "ModificatorFields",
            },
        },
        (data) => {
            productModificators.value = data.product_modificator_list;
        }
    );

    return {
        getProductModificator,
        checkModificatorCondition,
        productModificators,
        productModificatorsById,
        productModificatorsByProductId,
        productModificatorsByCodePath,
    };
});
